import { useMemo } from "react";

import type { GridRowsProp } from "@mui/x-data-grid";

import { Device } from "../../../API";
import { useGetNodes } from "./useGetNodes";
import {
  useSelectedLocationDropdown,
  useSelectedTagLocationsDropdown,
} from "../../../common/variables/selectedLocationDropdown";
import { useListenToNodeOnlineStatusChange } from "../../deployment-health/hooks/useListenToNodeHealthDataChange";

export interface NodesTableRowsDataInterface {
  index?: number | null;
  rowId: string;
  nodeId: string | null;
  devices: Device[] | null;
  nodeName?: string | null;
  location?: string | null;
  serviceData?: string | null;
  isOnline: boolean;
}

interface NodesTableRowsInterface {
  rows: GridRowsProp<NodesTableRowsDataInterface>;
  loading: boolean;
}

export const useNodeTableRows = (): NodesTableRowsInterface => {
  const selectedLocation = useSelectedLocationDropdown();
  const selectedTagLocations = useSelectedTagLocationsDropdown();

  const { data: nodesData, loading: getNodesLoading } = useGetNodes();

  const { data: nodeHealthData } = useListenToNodeOnlineStatusChange();

  const rowsByCustomer = useMemo((): NodesTableRowsDataInterface[] => {
    const mappedData =
      nodesData?.getNodes?.items
        ?.map((item, index: number): NodesTableRowsDataInterface => {
          return {
            index: index + 1,
            rowId: item?.id ?? "",
            nodeId: item?.nodeId ?? "",
            devices: item?.devices ?? [],
            nodeName: item?.nodeName,
            location: item?.locationId,
            nodeIP: item?.nodeIP,
            referenceId: item?.referenceId,
            serviceData: item?.serviceData,
            ...item,
            isOnline: !!item?.isOnline,
          };
        })
        .filter((item): NodesTableRowsDataInterface => item) ?? [];

    return mappedData.map((item, i) => ({ ...item, index: i + 1 }));
  }, [
    nodesData?.getNodes?.items,
    getNodesLoading,
    selectedLocation?.value,
    selectedTagLocations?.length,
  ]);

  const rows = useMemo(() => {
    return rowsByCustomer;
  }, [selectedLocation?.value, selectedTagLocations?.length, rowsByCustomer]);

  const memoizedRows = useMemo(() => {
    const isNodeDataFromSubscription = nodeHealthData?.listenToNodeOnlineStatus;

    if (!isNodeDataFromSubscription) return rows;

    const updatedRows = rows.map(node => {
      if (isNodeDataFromSubscription.id === node?.rowId) {
        return {
          ...node,
          isOnline: isNodeDataFromSubscription.isOnline ?? false,
        };
      }

      return { ...node };
    });

    return updatedRows;
  }, [nodeHealthData, rows]);

  // Determine loading state based on which query is active
  const loading = useMemo(() => {
    // If no location is selected, don't show loading
    if (!selectedLocation?.value) {
      return false;
    }

    return getNodesLoading;
  }, [selectedLocation?.value, getNodesLoading]);

  return { rows: memoizedRows, loading };
};
